/**
 * /operations/loans/active-register — Session 3a PR 11.
 *
 * All open loans across the LE with outstanding balances and arrears
 * flags. Tashghil-owned per Session 3a §4.10. Mutaba'aa surfaces the
 * Article 90 alerts.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Tashghil narrates exposure)
 *   2. InlineAISuggestion cards (Mutaba'aa cap + Khidmat reminder)
 *   3. TraceableValue wrapping outstanding + monthly_installment +
 *      article_90_headroom (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { Send, ShieldAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type ActiveLoanItem,
} from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "./_session3aHelpers";

const HEADROOM_CRITICAL = 10;

type Filter = "all" | "on_track" | "one_missed" | "arrears" | "near_cap";

function loanStatusTone(row: ActiveLoanItem): "neutral" | "attention" | "critical" {
  if (row.consecutive_missed >= 3) return "critical";
  if (row.consecutive_missed >= 1) return "attention";
  if (row.article_90_headroom_pct < HEADROOM_CRITICAL) return "critical";
  return "neutral";
}

export function ActiveLoansRegisterPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [filter, setFilter] = useState<Filter>("all");

  const list = useQuery({
    queryKey: ["active-loans", legalEntityId],
    queryFn: () => session3aApi.listActiveLoans(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: ActiveLoanItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const arrears = items.filter((r) => r.consecutive_missed >= 3);
    const nearCap = items.filter((r) => r.article_90_headroom_pct < HEADROOM_CRITICAL);
    const sumOut = items.reduce((acc, r) => acc + r.outstanding_sar, 0);
    return {
      total: items.length,
      arrears: arrears.length,
      nearCap: nearCap.length,
      sumOut,
      avgOut: items.length > 0 ? sumOut / items.length : 0,
      capCritical: items.find((r) => r.article_90_headroom_pct < HEADROOM_CRITICAL),
    };
  }, [items]);

  const filtered = useMemo(() => {
    switch (filter) {
      case "on_track":
        return items.filter((r) => r.consecutive_missed === 0);
      case "one_missed":
        return items.filter((r) => r.consecutive_missed === 1);
      case "arrears":
        return items.filter((r) => r.consecutive_missed >= 3);
      case "near_cap":
        return items.filter((r) => r.article_90_headroom_pct < HEADROOM_CRITICAL);
      default:
        return items;
    }
  }, [items, filter]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.loans_active.title", "Active Loans Register")}
          subtitle={t(
            "operations.loans_active.subtitle",
            "All open loans across the LE with outstanding balances and arrears flags. Tashghil tracks the register; Mutaba'aa watches Article 90 headroom.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["all", "All"],
                ["on_track", "On track"],
                ["one_missed", "1 missed"],
                ["arrears", "2+ missed"],
                ["near_cap", "Near Article 90 cap"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(filter === key ? "all" : (key as Filter))}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  filter === key
                    ? key === "arrears" || key === "near_cap"
                      ? "border-rose-300 bg-rose-50 text-rose-800"
                      : "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`operations.loans_active.filter.${key}`, label)}
              </button>
            ))}
          </div>
          <Link
            to="/operations/loans/applications"
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            {t("operations.loans_active.cta.new", "+ New loan")}
          </Link>
        </div>

        {stats.capCritical && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<ShieldAlert className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "operations.loans_active.suggestion.cap_headline",
              `Mutaba'aa flagged: ${stats.capCritical.employee_name_en} at ${(100 - stats.capCritical.article_90_headroom_pct).toFixed(0)}% of Article 90 cap — pending salary advance would breach.`,
            )}
            body={t(
              "operations.loans_active.suggestion.cap_body",
              "Escalate to HR or restructure installments to clear headroom before next payroll.",
            )}
            primaryAction={{
              label: t(
                "operations.loans_active.suggestion.open_watch",
                "Open Article 90 Watch",
              ),
              onClick: () =>
                window.location.assign("/operations/loans/article-90-watch"),
            }}
            suggestionKey={`active-loans-cap-${stats.capCritical.employee_id}`}
          />
        )}
        {items.some((r) => r.consecutive_missed === 1) && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<Send className="h-4 w-4" />}
            severity="info"
            headline={t(
              "operations.loans_active.suggestion.reminder_headline",
              "Soft reminder cadence.",
            )}
            body={t(
              "operations.loans_active.suggestion.reminder_body",
              "Single-missed loans go to soft Khidmat reminder before formal arrears (3+ missed).",
            )}
            primaryAction={{
              label: t(
                "operations.loans_active.suggestion.send_reminders",
                "Send reminders",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "operations.loans_active.suggestion.reminder_note",
                    "Khidmat batch reminder activates with Twilio creds in the M8 vault.",
                  ),
                ),
            }}
            suggestionKey="active-loans-soft-reminders"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.application", "Application")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.employee", "Employee")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.product", "Product")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.outstanding", "Outstanding (SAR)")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.installment", "Monthly")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.remaining", "Months left")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.headroom", "Art. 90 headroom")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.last_payment", "Last payment")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_active.col.status", "Status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const tone = loanStatusTone(row);
                  return (
                    <tr
                      key={row.application_id}
                      className="border-t border-border-subtle hover:bg-surface-sunken/40"
                    >
                      <td className="px-3 py-2">
                        <Link
                          to={`/operations/loans/applications/${row.application_id}`}
                          className="font-medium text-[#2260C9] hover:underline"
                        >
                          {row.application_id.slice(0, 8)}…
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/people/employees/${row.employee_id}`}
                          className="text-text-secondary hover:underline"
                        >
                          {row.employee_name_en}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-text-secondary">{row.product_name_en}</td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.loans_active.trace.outstanding",
                                `outstanding = principal − installments_paid + interest_accrued (if product carries rate). Source: loan_installments aggregation.`,
                              ),
                            )
                          }
                        >
                          <span
                            dir="ltr"
                            className={`font-medium ${
                              tone === "critical"
                                ? "text-rose-700"
                                : tone === "attention"
                                  ? "text-amber-700"
                                  : "text-text-primary"
                            }`}
                          >
                            {formatSar(row.outstanding_sar)}
                          </span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.loans_active.trace.installment",
                                `monthly_installment = approved_amount / tenure_months (last absorbs penny rounding).`,
                              ),
                            )
                          }
                        >
                          <span dir="ltr">{formatSar(row.monthly_installment_sar)}</span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2 text-text-secondary">{row.months_remaining}</td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.loans_active.trace.headroom",
                                `headroom_pct = 1 − (current_deductions / Article 90 cap). Cap = 1/3 of monthly wage; Mutaba'aa enforces.`,
                              ),
                            )
                          }
                        >
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              row.article_90_headroom_pct < HEADROOM_CRITICAL
                                ? severityChip("critical")
                                : row.article_90_headroom_pct < 20
                                  ? severityChip("attention")
                                  : severityChip("info")
                            }`}
                          >
                            {row.article_90_headroom_pct.toFixed(0)}%
                          </span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2 text-xs text-text-secondary">
                        {row.last_payment_status === "skipped"
                          ? t(
                              "operations.loans_active.skipped",
                              `Skipped · ${row.consecutive_missed}`,
                            )
                          : `${row.last_payment_status} · ${row.last_payment_date?.slice(0, 10) ?? "—"}`}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                            row.status === "in_repayment" ? "neutral" : "critical",
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.loans_active.empty",
              "No active loans in this LE. ✦ Tashghil notes: workforce is debt-free this period.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: stats.total,
            attention: stats.arrears,
            critical: stats.nearCap,
            top_kind: t("operations.loans_active.title", "Active Loans Register"),
            window_label: "active",
          }}
        />
      </aside>
    </div>
  );
}
